import { useThree, useFrame } from "@react-three/fiber";
import { useRef, useEffect, useState } from "react";
import * as THREE from "three";

export function ScaleControls({
  object,
  size = 2,
  baseColor = "red",
  hoverColor = "orange",
  visible = true,
  onMouseUp,
  onMouseDown,
  gizmoRef,
  onObjectChange,
  scaleSnap,
}) {
  const { camera, gl } = useThree();
  const cubeRef = useRef();
  const lineRef = useRef();
  const raycaster = useRef(new THREE.Raycaster()).current;
  const pointer = useRef(new THREE.Vector2()).current;

  const dragging = useRef(false);
  const lastPointerX = useRef(0);
  const [hovered, setHovered] = useState(false);

  // Attach global listeners once
  useEffect(() => {
    function getPointer(event) {
      const rect = gl.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    }

    function onPointerMove(event) {
      getPointer(event);

      // check hover
      raycaster.setFromCamera(pointer, camera);
      if (cubeRef.current) {
        const hits = raycaster.intersectObject(cubeRef.current, false);

        if (!dragging.current) setHovered(hits.length > 0 || dragging.current);
      }

      // drag
      if (dragging.current && object) {
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
        const movementX = event.clientX - lastPointerX.current;
        lastPointerX.current = event.clientX;
        const delta = movementX * 0.03;
        const factor = 1 + delta;
        if (factor) {
          // const currentScale = gizmoRef.current.scale.x;
          // const newScale = currentScale * factor;
          const snappedScale = Math.round(factor / scaleSnap) * scaleSnap;
          const safeScale = Math.max(snappedScale, 0.01);

          gizmoRef.current.scale.multiplyScalar(safeScale);

          onObjectChange?.();
        }
      }
    }

    function onPointerDown(event) {
      getPointer(event);
      raycaster.setFromCamera(pointer, camera);

      if (cubeRef.current) {
        const hits = raycaster.intersectObject(cubeRef.current, false);
        if (hits.length > 0) {
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          dragging.current = true;
          lastPointerX.current = event.clientX;
          onMouseDown?.();
        }
      }
    }

    function onPointerUp(event) {
      if (dragging.current) {
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
        dragging.current = false;
        onMouseUp?.();
      }
    }

    gl.domElement.addEventListener("pointermove", onPointerMove);
    gl.domElement.addEventListener("pointerdown", onPointerDown);
    gl.domElement.addEventListener("pointerup", onPointerUp);

    return () => {
      gl.domElement.removeEventListener("pointermove", onPointerMove);
      gl.domElement.removeEventListener("pointerdown", onPointerDown);
      gl.domElement.removeEventListener("pointerup", onPointerUp);
    };
  }, [
    camera,
    gl,
    object,
    raycaster,
    pointer,
    lastPointerX,
    gizmoRef,
    onObjectChange,
    onMouseUp,
    onMouseDown,
    scaleSnap,
  ]);

  useFrame(() => {
    if (!object || !cubeRef.current || !lineRef.current) return;
    if (dragging.current && !hovered) setHovered(true);
    const objPos = object.position.clone();
    const dir = new THREE.Vector3()
      .subVectors(camera.position, objPos)
      .normalize();
    const distanceToCamera = objPos.distanceTo(camera.position);

    // screen-space scale
    const scaleFactor = distanceToCamera * 0.015;
    cubeRef.current.scale.setScalar(scaleFactor);

    // offset diagonally in XZ plane (45 degrees between X and Z axes)
    const offset = distanceToCamera * 0.18;
    const xSign = Math.sign(dir.x) || 1;
    const zSign = Math.sign(dir.z) || 1;
    const diag = offset * Math.SQRT1_2; // 1/√2 for 45-degree split
    const cubePos = objPos.clone().add(new THREE.Vector3(diag * xSign, 0, diag * zSign));
    cubeRef.current.position.copy(cubePos);
    cubeRef.current.updateMatrixWorld(true);

    // axis line
    lineRef.current.geometry.setFromPoints([objPos, cubePos]);
  });

  if (!visible || !object) return null;

  return (
    <group>
      <line ref={lineRef}>
        <bufferGeometry />
        <lineBasicMaterial
          color={hovered ? hoverColor : baseColor}
          linewidth={2}
          depthTest={false} // always render on top
        />
      </line>
      <mesh ref={cubeRef}>
        <boxGeometry args={[size, size, size]} />
        <meshBasicMaterial
          depthTest={false} // always render on top
          depthWrite={false}
          color={hovered || dragging.current ? hoverColor : baseColor}
        />
      </mesh>
    </group>
  );
}
